import { PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import { Upload, message } from "antd";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import { AppButton, AppTextArea } from "components/antd-ui";
import { debounce } from "lodash";
import { memo, useCallback, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { staffApi } from "api/staff.api";
import type { CommentFile, CommentStaff } from "types/comment";
import { Staff } from "types/staff";
import { getToken } from "utils/auth";
import { $url } from "utils/url";
import { commentAllowExtensions, commentUploadUrl, isCommentImage } from "./commentView";

type CommentComposerProps = {
  avatar?: string;
  name: string;
  submitLabel: string;
  placeholder: string;
  onSubmit: (payload: { content: string; files: CommentFile[]; mentionStaffIds: number[] }) => Promise<void>;
};

const toStaff = (staff: Staff): CommentStaff => ({
  id: staff.id,
  name: staff.name,
  avatar: staff.avatar,
  isBlocked: staff.isBlocked,
});

export const CommentComposer = memo(({ avatar, name, submitLabel, placeholder, onSubmit }: CommentComposerProps) => {
  const [content, setContent] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionOptions, setMentionOptions] = useState<CommentStaff[]>([]);
  const [mentions, setMentions] = useState<CommentStaff[]>([]);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const mentionsRef = useRef<CommentStaff[]>([]);
  mentionsRef.current = mentions;

  const searchStaff = useMemo(
    () =>
      debounce(async (keyword: string) => {
        const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword, isBlocked: false });
        const staffs: Staff[] = res.data.staffs || [];
        const options = staffs.filter((staff) => !staff.isBlocked).map(toStaff);
        const ids = new Set(options.map((staff) => staff.id));
        const kept = mentionsRef.current.filter(
          (staff) => !ids.has(staff.id) && staff.name.toLowerCase().includes(keyword.toLowerCase()),
        );
        setMentionOptions([...options, ...kept]);
      }, 500),
    [],
  );

  const syncMention = useCallback(
    (value: string, caret: number) => {
      const match = value.slice(0, caret).match(/@([^\s@]*)$/);
      if (!match) {
        setMentionOpen(false);
        return;
      }
      setMentionOpen(true);
      searchStaff(match[1]);
    },
    [searchStaff],
  );

  const handleContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.currentTarget.value;
      setContent(value);
      syncMention(value, event.currentTarget.selectionStart ?? value.length);
    },
    [syncMention],
  );

  const handleSelectMention = useCallback(
    (staff: CommentStaff) => {
      const caret = textRef.current?.selectionStart ?? content.length;
      const before = content.slice(0, caret).replace(/@([^\s@]*)$/, `@${staff.name} `);
      const after = content.slice(caret);
      const next = `${before}${after}`;
      setContent(next);
      setMentions((prev) => (prev.some((item) => item.id === staff.id) ? prev : [...prev, staff]));
      setMentionOpen(false);
      requestAnimationFrame(() => {
        const current = textRef.current as (HTMLTextAreaElement & {
          resizableTextArea?: { textArea: HTMLTextAreaElement };
        }) | null;
        const textArea = current?.resizableTextArea?.textArea || current;
        textArea?.focus();
        const nextCaret = before.length;
        textArea?.setSelectionRange(nextCaret, nextCaret);
      });
    },
    [content],
  );

  const handleUpload = useCallback((info: UploadChangeParam) => {
    setFileList(info.fileList);
    if (info.file.status === "error") {
      message.error("Upload thất bại");
    }
  }, []);

  const beforeUpload = useCallback((file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!commentAllowExtensions.includes(ext)) {
      message.error("Định dạng file không hỗ trợ");
      return Upload.LIST_IGNORE;
    }
    return true;
  }, []);

  const removeFile = useCallback((uid: string) => {
    setFileList((prev) => prev.filter((file) => file.uid !== uid));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (sendingRef.current) return;

    const files: CommentFile[] = fileList
      .map((file) => {
        const response = file.response as { data?: { path?: string } } | undefined;
        return {
          name: file.name,
          url: file.url || response?.data?.path || "",
          status: file.status,
        };
      })
      .filter((file) => file.status === "done" && file.url)
      .map((file) => ({ name: file.name, url: file.url }));

    const text = content.trim();
    if (!text && !files.length) {
      message.warning("Nhập nội dung hoặc đính kèm file");
      return;
    }
    if (fileList.some((file) => file.status === "uploading")) {
      message.warning("File đang tải lên");
      return;
    }

    const mentionStaffIds = mentions
      .filter((staff) => text.includes(`@${staff.name}`))
      .map((staff) => staff.id);

    sendingRef.current = true;
    setSending(true);
    try {
      await onSubmit({ content: text, files, mentionStaffIds });
      setContent("");
      setFileList([]);
      setMentions([]);
      setMentionOpen(false);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [content, fileList, mentions, onSubmit]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || !event.shiftKey) return;
    event.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  return (
    <div className="commentThread__composer">
      <CommentAvatar avatar={avatar} name={name} />
      <div className="commentThread__composerBody">
        <AppTextArea
          ref={textRef}
          value={content}
          rows={3}
          placeholder={placeholder}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          onKeyUp={(event) => syncMention(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
          onClick={(event) => syncMention(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
        />
        {mentionOpen && (
          <div className="commentThread__mentions">
            {mentionOptions.length ? (
              mentionOptions.map((staff) => (
                <button
                  type="button"
                  className="commentThread__mentionOption"
                  key={staff.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectMention(staff)}
                >
                  <CommentAvatar avatar={staff.avatar} name={staff.name} />
                  <span>{staff.name}</span>
                </button>
              ))
            ) : (
              <div className="commentThread__mentionEmpty">Không có nhân viên</div>
            )}
          </div>
        )}
        {!!fileList.length && (
          <div className="commentThread__pending">
            {fileList.map((file) => {
              const response = file.response as { data?: { path?: string } } | undefined;
              const url = file.url || response?.data?.path || "";
              const image = isCommentImage(file.name) && !!url && file.status === "done";
              return (
                <div className="commentThread__pendingFile" key={file.uid}>
                  {image ? <img src={$url(url)} alt="" /> : null}
                  <span>{file.status === "uploading" ? "Đang tải..." : file.name}</span>
                  <button type="button" onClick={() => removeFile(file.uid)} aria-label="Bỏ file">
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="commentThread__composerActions">
          <AppButton
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSubmit}
          >
            {submitLabel}
            <span className="commentThread__shortcut">Shift + Enter</span>
          </AppButton>
          <Upload
            multiple
            showUploadList={false}
            action={commentUploadUrl}
            headers={{ token: getToken() }}
            accept={commentAllowExtensions.map((ext) => `.${ext}`).join(",")}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleUpload}
          >
            <button type="button" className="commentThread__clip" aria-label="Đính kèm">
              <PaperClipOutlined />
            </button>
          </Upload>
        </div>
      </div>
    </div>
  );
});

CommentComposer.displayName = "CommentComposer";

export const CommentAvatar = ({ avatar, name }: { avatar?: string; name: string }) => {
  const src = avatar ? $url(avatar) : "";
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <span className="commentThread__avatar">
      {src ? <img src={src} alt="" /> : <span>{initial}</span>}
    </span>
  );
};
